import { keepPreviousData, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { capitalize } from "lodash";
import {
  Card,
  CardContent,
  TooltipCore,
  TooltipContent,
  TooltipTrigger,
  Typography,
} from "@altimateai/lego";
import { ComponentLoader } from "@/components/loader";
import { DataTable } from "@/components/tables";
import { RectColorTag } from "@/components/tags";
import { getDbtCloudSyncHistory } from "@/helpers/apis";
import { DBT_CLOUD_QUERY_KEYS, DbtCloudProcessedRunResponse } from "../../types";
import { TableHeaderType } from "@/components/tables/types";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { Paginate } from "@/components/paginate";

interface SyncHistoryProps {
  connectionId: number;
}

type SyncHistoryItem = DbtCloudProcessedRunResponse & { id: number };

const DEFAULT_PAGE_SIZE = 20;

const SyncHistory = ({ connectionId }: SyncHistoryProps): JSX.Element => {
  const { page, pageSize, setPage, setPageSize } = useTablePagination();

  // Build pagination params for API call
  const paginationParams = {
    page: page + 1, // API expects 1-based indexing
    size: pageSize || DEFAULT_PAGE_SIZE,
  };

  const {
    data: syncHistory,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [DBT_CLOUD_QUERY_KEYS.SYNC_HISTORY, connectionId, paginationParams],
    queryFn: () => getDbtCloudSyncHistory(connectionId, paginationParams),
    placeholderData: keepPreviousData,
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const headers: TableHeaderType<SyncHistoryItem>[] = [
    {
      id: 1,
      label: "Run ID",
      uid: "run_id",
      width: "10%",
      render: item => (
        <Typography size="sm" weight="regular" className="al-break-words">
          {item?.dbt_cloud_run_id || ""}
        </Typography>
      ),
    },
    {
      id: 2,
      label: "Project",
      uid: "project",
      width: "12%",
      render: item => (
        <Typography size="sm" className="al-break-words">
          {item?.project_name || "-"}
        </Typography>
      ),
    },
    {
      id: 3,
      label: "Environment",
      uid: "environment",
      width: "12%",
      render: item => (
        <Typography size="sm" className="al-break-words">
          {item?.environment_name || "-"}
        </Typography>
      ),
    },
    {
      id: 4,
      label: "Run Finished",
      uid: "run_finished",
      width: "14%",
      render: item => (
        <Typography size="sm" className="al-break-words">
          {item?.run_finished_at ? dayjs(item.run_finished_at).format("h:mm A, D MMMM YY") : ""}
        </Typography>
      ),
    },
    {
      id: 5,
      label: "Processed",
      uid: "processed",
      width: "14%",
      render: item => (
        <Typography size="sm" className="al-break-words">
          {item?.processed_at ? dayjs(item.processed_at).format("h:mm A, D MMMM YY") : ""}
        </Typography>
      ),
    },
    {
      id: 6,
      label: "Status",
      uid: "status",
      width: "10%",
      render: item => (
        <div>
          {item?.status && (
            <RectColorTag
              label={capitalize(item.status)}
              color={
                item.status === "success"
                  ? "lightGreen"
                  : item.status === "failed"
                    ? "pink"
                    : "purple"
              }
            />
          )}
        </div>
      ),
    },
    {
      id: 7,
      label: "Comments",
      uid: "comments",
      width: "28%",
      render: item => {
        const getComment = (): string => {
          const details = item?.sync_details;

          // If we have sync_details, use it for detailed comments
          if (details) {
            const parts: string[] = [];

            // Show synced files
            if (details.synced_files && details.synced_files.length > 0) {
              parts.push(`Synced: ${details.synced_files.join(", ")}`);
            }

            // Show failed files with errors
            if (details.failed_files && details.failed_files.length > 0) {
              const failedInfo = details.failed_files.map(f => `${f.name} (${f.error})`).join("; ");
              parts.push(`Failed: ${failedInfo}`);
            }

            // Show missing artifacts (not available from dbt Cloud)
            if (details.missing_artifacts && details.missing_artifacts.length > 0) {
              parts.push(`Not available: ${details.missing_artifacts.join(", ")}`);
            }

            // Show ingestion error if present
            if (details.ingestion_error) {
              parts.push(`Ingestion error: ${details.ingestion_error}`);
            }

            // Show general errors
            if (details.errors && details.errors.length > 0) {
              parts.push(`Errors: ${details.errors.join("; ")}`);
            }

            if (parts.length > 0) {
              return parts.join(" | ");
            }
          }

          // Fallback to error_message or status-based messages
          if (item?.error_message) return item.error_message;
          if (item?.status === "failed") return "Sync failed";
          if (item?.status === "partial") return "Partial sync - some artifacts failed";
          return "Sync was successful";
        };

        const comment = getComment();

        return (
          <TooltipCore>
            <TooltipTrigger asChild>
              <Typography
                size="sm"
                className="al-text-muted-foreground al-line-clamp-2 al-cursor-default"
              >
                {comment}
              </Typography>
            </TooltipTrigger>
            <TooltipContent side="top" className="al-max-w-md">
              <Typography size="sm">{comment}</Typography>
            </TooltipContent>
          </TooltipCore>
        );
      },
    },
  ];

  if (isLoading) {
    return <ComponentLoader label="Loading sync history..." />;
  }

  const runs: SyncHistoryItem[] = syncHistory?.recent_runs || [];
  const totalItems = syncHistory?.total || 0;
  const totalPages = syncHistory?.pages || 1;
  const currentPageSize = syncHistory?.size || pageSize || DEFAULT_PAGE_SIZE;

  return (
    <Card>
      <CardContent className="al-pt-4 al-pl-4">
        <Typography variant="section-heading" className="al-mb-2">
          dbt Cloud Project Environment Run History
        </Typography>
        <DataTable<SyncHistoryItem>
          header={headers}
          data={{ items: runs }}
          idKey={item => `${item.dbt_cloud_run_id}-${item.processed_at}-${item.id}`}
          isLoading={isFetching}
        />
        {runs.length === 0 && !isFetching && (
          <Typography className="al-text-center al-text-muted-foreground al-py-8">
            No sync history available. Trigger a sync to start processing artifacts.
          </Typography>
        )}
        {totalItems > 0 && (
          <Paginate
            itemCount={totalItems}
            page={page}
            pageSize={currentPageSize}
            numPages={totalPages}
            onPageClick={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showPageSize
            isLoading={isFetching}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default SyncHistory;
